import type { Auth0Client, IdToken } from '@auth0/auth0-spa-js';

import { Auth0CallbackPath } from '../../../constants/auth0.constants';
import { getAuth0Config } from '../../account.service';
import { getFeatureFlagValue } from '../../featureFlags/getFeatureFlagValue';
import { PortalFeatureFlags } from '../../featureFlags/portalFeatureFlags';

/**
 * Browser-side Auth0 ID token layer (the portal "cutover").
 *
 * While `frontend_portal_auth0IdTokenCutover` is off (or unreadable), every function here
 * fails safe to the legacy behaviour: no client is created, no token is acquired, and the
 * transport layer finds no header. When it is on, login is a full-page auth0-spa-js PKCE
 * redirect against the dedicated Portal SPA Auth0 app, the browser holds the 1h ID token
 * plus the rotating 90-day refresh token, and API calls present the ID token directly.
 * See doc/adr/0008-portal-auth0-id-token-cutover.md.
 */

const ERROR_PREFIX = '[IdToken]';
const ID_TOKEN_SCOPE = 'openid profile email offline_access';
// Tokens expiring within the next 30s are treated as expired so requests never race the
// expiry moment.
const CLOCK_SKEW_SECONDS = 30;
// The ID token is also mirrored into an FD-Authorization cookie: Zeus, RMS and
// authorization-service all validate an ID token presented on that cookie, and it is the only
// transport that reaches the cross-document surfaces a header cannot — the RMS backoffice
// iframe, file downloads and the SignalR negotiate. Written client-side (the portal origin is
// same-site with those hosts), refreshed on every acquisition/renewal, cleared on logout and
// whenever the cutover flag reads off. Not HttpOnly — the token already lives in local storage
// via the SDK cache, so the exposure class is unchanged.
const ID_TOKEN_COOKIE_NAME = 'FD-Authorization';
const ID_TOKEN_COOKIE_ATTRIBUTES = 'path=/; secure; samesite=lax';

export function isAuth0IdTokenCutoverEnabled(): boolean {
  // Read per call: the flag doubles as the kill switch and must take effect on the next
  // request, not on the next page load. A failed read (no provider, OpenFeature error)
  // fails safe to the legacy path — this function must never throw, it gates the login,
  // the callback, the transport layer and the bootstrap probe.
  try {
    return getFeatureFlagValue(PortalFeatureFlags.auth0IdTokenCutover, false);
  } catch (error) {
    console.error(`${ERROR_PREFIX} Failed to read the cutover flag; treating as off.`, error);
    return false;
  }
}

function redirectUri(): string {
  return `${window.location.origin}${Auth0CallbackPath}`;
}

// Explicit parent-domain allow-list instead of a generic "last two labels" guess — a public
// suffix (e.g. a .com.au host) would produce a cookie domain that is not a parent of the host
// at all. Unknown hosting (local dev, ephemeral origins) falls back to a host-only cookie.
function deriveCookieDomain(): string | undefined {
  const hostname = window.location.hostname;
  if (hostname.endsWith('.flipdish.com')) {
    return '.flipdish.com';
  }
  if (hostname.endsWith('.flipdishdev.com')) {
    return '.flipdishdev.com';
  }
  return undefined;
}

/**
 * Whether the ID-token cookie can function from the current origin. When it cannot (local
 * development, unknown hosting), the flag-on login would produce a credential no API call
 * can carry — so callers must fall back to the classic flow instead of starting it.
 */
export function canUseIdTokenCookie(): boolean {
  return deriveCookieDomain() !== undefined;
}

function writeIdTokenCookie(raw: string, expiresAtSeconds: number): void {
  const domain = deriveCookieDomain();
  const maxAgeSeconds = Math.max(0, expiresAtSeconds - Math.floor(Date.now() / 1000));
  document.cookie = [
    `${ID_TOKEN_COOKIE_NAME}=${raw}`,
    `max-age=${maxAgeSeconds}`,
    ID_TOKEN_COOKIE_ATTRIBUTES,
    domain ? `domain=${domain}` : null,
  ]
    .filter(Boolean)
    .join('; ');
}

function clearIdTokenCookie(): void {
  const domain = deriveCookieDomain();
  document.cookie = [
    `${ID_TOKEN_COOKIE_NAME}=`,
    'max-age=0',
    ID_TOKEN_COOKIE_ATTRIBUTES,
    domain ? `domain=${domain}` : null,
  ]
    .filter(Boolean)
    .join('; ');
}

// ---------------------------------------------------------------------------
// Client lifecycle
// ---------------------------------------------------------------------------

let auth0Client: Auth0Client | null = null;
let clientPromise: Promise<Auth0Client | null> | null = null;

async function createClient(): Promise<Auth0Client> {
  // Dynamic import keeps the SDK out of the initial bundle while the cutover is off.
  const { Auth0Client: Auth0ClientConstructor } = await import('@auth0/auth0-spa-js');

  const clientId = process.env.VITE_AUTH0_SPA_CLIENT_ID;
  if (!clientId) {
    throw new Error(
      `${ERROR_PREFIX} VITE_AUTH0_SPA_CLIENT_ID is not configured; the ID token layer stays disabled.`
    );
  }

  // Same Auth0 tenant as the classic flow — reuse its domain resolution.
  const { Auth0Domain: domain } = await getAuth0Config();
  if (!domain) {
    throw new Error(`${ERROR_PREFIX} LoginConfig did not return an Auth0 domain.`);
  }

  return new Auth0ClientConstructor({
    domain,
    clientId,
    cacheLocation: 'localstorage',
    useRefreshTokens: true,
    authorizationParams: {
      redirect_uri: redirectUri(),
      scope: ID_TOKEN_SCOPE,
    },
  });
}

/**
 * Resolves the shared auth0-spa-js client, or null when the cutover is disabled or the
 * client cannot be created (missing config, SDK failure). Never throws.
 */
async function getClient(): Promise<Auth0Client | null> {
  if (!isAuth0IdTokenCutoverEnabled()) {
    return null;
  }

  if (auth0Client) {
    return auth0Client;
  }

  if (!clientPromise) {
    clientPromise = createClient()
      .then((client) => {
        auth0Client = client;
        return client;
      })
      .catch((error: unknown) => {
        console.error(`${ERROR_PREFIX} Failed to create the auth0-spa-js client.`, error);
        return null;
      });
  }

  return clientPromise;
}

// ---------------------------------------------------------------------------
// Token access
// ---------------------------------------------------------------------------

// Sync mirror of the SDK's cached ID token, refreshed after every async token operation.
// getIdTokenClaims() is async in the current SDK while the transport layer needs a
// synchronous read per request, so this is the module's single source of truth for it.
let cachedToken: { raw: string; expiresAtSeconds: number } | null = null;

function isExpired(expiresAtSeconds: number): boolean {
  return expiresAtSeconds - CLOCK_SKEW_SECONDS <= Date.now() / 1000;
}

function cacheFromClaims(claims: IdToken): void {
  if (typeof claims.__raw !== 'string' || typeof claims.exp !== 'number') {
    return;
  }
  cachedToken = { raw: claims.__raw, expiresAtSeconds: claims.exp };
  keepAliveFailures = 0;
  // Only invoked on flag-on paths (renewal and the login callback), so the cookie is written
  // exactly when the cutover is active.
  writeIdTokenCookie(claims.__raw, claims.exp);
  scheduleRenewal();
}

function readCachedToken(): string | null {
  if (!cachedToken || isExpired(cachedToken.expiresAtSeconds)) {
    return null;
  }
  return cachedToken.raw;
}

let renewalInFlight: Promise<string | null> | null = null;
// Incremented by every clearIdTokenSession: in-flight renewals compare their start
// epoch against this and discard their result when a logout happened mid-flight.
let logoutEpoch = 0;

/**
 * Synchronously returns the cached raw ID token, or null when the cutover is off, the
 * client has not been established yet, or the cached token has expired. Used per request
 * by the transport layer — it never blocks; an expired token is renewed by
 * {@link getValidIdToken}, whose result later requests pick up.
 */
export function getCachedIdToken(): string | null {
  if (!isAuth0IdTokenCutoverEnabled()) {
    return null;
  }
  return readCachedToken();
}

/**
 * Returns a valid raw ID token, renewing it through the refresh grant when the cached
 * one is stale. Concurrent callers share a single renewal. Resolves null when the
 * cutover is off or renewal fails — callers then fall back to cookie-only behaviour.
 */
export async function getValidIdToken(): Promise<string | null> {
  const cached = getCachedIdToken();
  if (cached) {
    return cached;
  }

  // Capture before any await: a logout during client creation must invalidate the
  // renewal that this call is about to start.
  const epochAtStart = logoutEpoch;
  const client = await getClient();
  if (!client) {
    return null;
  }

  if (!renewalInFlight) {
    renewalInFlight = renewToken(client, epochAtStart);
  }
  return renewalInFlight;
}

async function renewToken(client: Auth0Client, epochAtStart: number): Promise<string | null> {
  try {
    // `useRefreshTokens` routes this through the refresh grant. With no refresh token
    // cached the SDK throws MissingRefreshTokenError rather than falling back to a
    // hidden iframe (useRefreshTokensFallback is not enabled) — that failure is handled
    // here as a plain no-token result and the self-healing ladder falls back to the
    // classic flow.
    await client.getTokenSilently({ cacheMode: 'off' });
    const claims = await client.getIdTokenClaims();
    if (!claims) {
      keepAliveFailures += 1;
      return null;
    }
    if (logoutEpoch !== epochAtStart) {
      // A logout ran while this renewal was in flight: the SDK has already re-saved
      // its cache entries and cacheFromClaims would re-write the cookie — discard
      // the result unless a genuinely newer credential exists (a new login or a
      // newer renewal), identified by the raw token.
      discardStaleRenewalResult(claims.__raw ?? null);
      return null;
    }
    cacheFromClaims(claims);
  } catch (error) {
    console.error(`${ERROR_PREFIX} Failed to renew the ID token.`, error);
    keepAliveFailures += 1;
    return null;
  } finally {
    renewalInFlight = null;
  }
  return readCachedToken();
}

// ---------------------------------------------------------------------------
// Mid-session renewal (keep-alive)
// ---------------------------------------------------------------------------

// The ID token lives ~1h. Renew it this many seconds before it expires so the mirrored
// cookie never lapses while the tab stays open. Background tabs throttle timers past
// this mark, so a visibility listener tops up the credential the moment the tab is
// seen again.
const RENEWAL_LEAD_SECONDS = 300;
const RENEWAL_RETRY_SECONDS = 60;
// A persistently failing renewal (e.g. a revoked refresh token) stops retrying after
// this many consecutive attempts instead of hammering the tenant forever.
const MAX_CONSECUTIVE_RENEWAL_FAILURES = 3;

let renewalTimer: ReturnType<typeof setTimeout> | null = null;
let keepAliveFailures = 0;
let visibilityListenerAttached = false;

function isExpiringWithin(seconds: number, expiresAtSeconds: number): boolean {
  return expiresAtSeconds - seconds <= Date.now() / 1000;
}

function armRenewalTimer(delaySeconds: number): void {
  if (renewalTimer) {
    clearTimeout(renewalTimer);
  }
  renewalTimer = setTimeout(() => {
    renewalTimer = null;
    void keepAliveRenewal();
  }, delaySeconds * 1000);
}

/**
 * Arms the next keep-alive cycle from the cached token. Inert while the cutover flag
 * reads off — the flag is the kill switch, so no timer may be armed (or re-armed) for
 * a session the feature no longer owns.
 */
function scheduleRenewal(): void {
  if (!isAuth0IdTokenCutoverEnabled()) {
    return;
  }
  attachVisibilityListener();
  if (keepAliveFailures >= MAX_CONSECUTIVE_RENEWAL_FAILURES) {
    console.error(
      `${ERROR_PREFIX} Keep-alive renewal failed ${keepAliveFailures} times in a row; giving up until the next acquisition or full reload.`
    );
    return;
  }
  const expiresAtSeconds = cachedToken?.expiresAtSeconds;
  if (expiresAtSeconds === undefined) {
    armRenewalTimer(RENEWAL_RETRY_SECONDS);
    return;
  }
  const secondsUntilRenewal = expiresAtSeconds - RENEWAL_LEAD_SECONDS - Date.now() / 1000;
  // A stale token left by a failed renewal computes a non-positive delay — retry on the
  // retry cadence instead of spinning.
  armRenewalTimer(secondsUntilRenewal > 0 ? secondsUntilRenewal : RENEWAL_RETRY_SECONDS);
}

function attachVisibilityListener(): void {
  if (visibilityListenerAttached || typeof document === 'undefined') {
    return;
  }
  visibilityListenerAttached = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible' || !isAuth0IdTokenCutoverEnabled()) {
      return;
    }
    const expiresAtSeconds = cachedToken?.expiresAtSeconds;
    if (
      expiresAtSeconds !== undefined &&
      isExpiringWithin(RENEWAL_LEAD_SECONDS, expiresAtSeconds)
    ) {
      void keepAliveRenewal();
    }
  });
}

async function keepAliveRenewal(): Promise<void> {
  // Re-checked at fire time: the flag is the kill switch and may have flipped since the
  // timer was armed.
  if (!isAuth0IdTokenCutoverEnabled()) {
    return;
  }
  // Capture before any await: a logout during client resolution must invalidate the
  // renewal this cycle is about to run.
  const epochAtStart = logoutEpoch;
  const client = await getClient();
  if (!client) {
    return;
  }
  if (!renewalInFlight) {
    renewalInFlight = renewToken(client, epochAtStart);
  }
  await renewalInFlight;
  scheduleRenewal();
}

// ---------------------------------------------------------------------------
// Login session
// ---------------------------------------------------------------------------

/**
 * Optional authorization parameters forwarded to the Auth0 `/authorize` redirect. They
 * keep feature parity with the classic start (`login_hint` prefills the email,
 * `screen_hint: 'signup'` routes new invitees to the signup screen, `ui_locales`
 * localises the hosted page).
 */
export type IdTokenLoginParams = {
  login_hint?: string;
  screen_hint?: string;
  ui_locales?: string;
};

/**
 * Starts the flag-on login: a full-page PKCE redirect through the dedicated Portal SPA
 * app. The browser receives the ID token and the rotating refresh token at the callback;
 * the classic Zeus code exchange is skipped entirely.
 */
export async function loginWithIdTokenRedirect(
  extraParams: IdTokenLoginParams = {}
): Promise<void> {
  const client = await getClient();
  if (!client) {
    throw new Error(
      `${ERROR_PREFIX} The ID token login is unavailable (cutover off or misconfigured).`
    );
  }
  await client.loginWithRedirect({
    authorizationParams: { ...extraParams, redirect_uri: redirectUri() },
  });
}

/**
 * Completes the flag-on login at {@link Auth0CallbackPath} and caches the received
 * tokens (including the rotating refresh token).
 */
export async function handleIdTokenLoginCallback(): Promise<void> {
  const client = await getClient();
  if (!client) {
    throw new Error(
      `${ERROR_PREFIX} The ID token login callback is unavailable (cutover off or misconfigured).`
    );
  }
  await client.handleRedirectCallback();
  const claims = await client.getIdTokenClaims();
  if (claims) {
    cacheFromClaims(claims);
  }
}

/**
 * Restores the token session on cold start / page load from the SDK's local storage
 * cache, renewing the ID token through the refresh grant when it has expired. Never
 * throws — a failure simply leaves no token and the transport layer stays cookie-only.
 */
export async function ensureIdTokenSession(): Promise<void> {
  // Skip entirely for browsers without a cached SDK session (e.g. logged-out visitors):
  // creating the client costs a LoginConfig round trip and the renewal would fail
  // anyway. The cache-prefix check is cheap and local. Any cookie a previous flag-on
  // session wrote is left alone — it self-expires with the token (≤1h) and must not be
  // confused with (or clear) a legacy classic-flow session cookie.
  if (!isAuth0IdTokenCutoverEnabled() || !hasCachedSdkSession()) {
    return;
  }
  try {
    await getValidIdToken();
  } catch (error) {
    console.error(`${ERROR_PREFIX} Failed to restore the ID token session.`, error);
  }
}

// auth0-spa-js keys its local-storage cache entries with this prefix; its presence is
// the cheapest signal that a browser held a token session.
const SDK_CACHE_KEY_PREFIX = '@@auth0spajs@@::';

/**
 * Whether the browser holds auth0-spa-js cache entries. Synchronous and flag-free —
 * usable before the feature-flag provider is ready (e.g. the cold-start probe).
 */
export function hasCachedSdkSession(): boolean {
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      if (window.localStorage.key(i)?.startsWith(SDK_CACHE_KEY_PREFIX)) {
        return true;
      }
    }
  } catch {
    // Storage unavailable (privacy mode, sandboxing) — let the renewal attempt decide.
    return true;
  }
  return false;
}

function removeSdkCacheEntries(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(SDK_CACHE_KEY_PREFIX)) {
        keys.push(key);
      }
    }
    keys.forEach((key) => window.localStorage.removeItem(key));
  } catch (error) {
    console.error(`${ERROR_PREFIX} Failed to clear the SDK storage cache.`, error);
  }
}

function discardStaleRenewalResult(staleRaw: string | null): void {
  const current = readCachedToken();
  if (current !== null && current !== staleRaw) {
    return; // a newer credential exists (a genuine new login / newer renewal)
  }
  cachedToken = null;
  removeSdkCacheEntries();
  clearIdTokenCookie();
}

/**
 * Clears the browser-side token session (SDK cache, storage and the ID-token cookie).
 * The destructive cleanup only runs when an ID-token session actually exists (in-memory
 * token, live client, or SDK storage cache) — a pure classic-flow logout must leave
 * document.cookie alone so the legacy credential the logout request itself may rely on
 * is never touched, and a flag flipped off mid-session still gets its cookie cleared by
 * the next logout (the SDK cache proves the ID-token session). The classic logout still
 * performs the Auth0 `/oidc/logout` redirect that ends the tenant SSO session.
 */
export function clearIdTokenSession(): void {
  // Invalidates any in-flight renewal: its result is discarded when it settles.
  logoutEpoch += 1;
  const hasIdTokenSession = cachedToken !== null || auth0Client !== null || hasCachedSdkSession();
  cachedToken = null;
  if (renewalTimer) {
    clearTimeout(renewalTimer);
    renewalTimer = null;
  }
  const client = auth0Client;
  auth0Client = null;
  clientPromise = null;
  if (!hasIdTokenSession) {
    return;
  }
  clearIdTokenCookie();
  if (client) {
    // The SDK's logout is async (it clears local storage); it is not awaited — the
    // classic logout redirect follows immediately. Promise.resolve makes this safe for
    // implementations that throw synchronously or return nothing.
    try {
      void Promise.resolve(client.logout({ openUrl: () => undefined })).catch((error: unknown) =>
        console.error(`${ERROR_PREFIX} Failed to clear the SDK session.`, error)
      );
    } catch (error) {
      console.error(`${ERROR_PREFIX} Failed to clear the SDK session.`, error);
    }
  } else {
    // No live client (flag off at load, client creation failed, or creation still
    // pending): the SDK cache still holds the rotating refresh token, so remove the
    // storage entries directly — otherwise the next flag-on cold start would redeem
    // the old refresh token and silently restore the logged-out user's session.
    removeSdkCacheEntries();
  }
}
